import { Group, Text } from "@mantine/core";
import { useState } from "react";

import type { VolumeOut } from "../../api/generated/model";
import { palette } from "../../palette";
import { fmt0, fmtPct, fmtRange } from "./tables";

/**
 * Volume per sector along the arc (sector 1 at the arc's start), the bar's whisker the range,
 * pale where much of the sector was unseen. Hover reads a sector out.
 */
export function SectorBars({ sectors }: { sectors: VolumeOut[] }) {
  const rows = sectors
    .filter((v) => v.scope === "sector")
    .sort((a, b) => Number(a.scope_id) - Number(b.scope_id));
  const [hover, setHover] = useState<VolumeOut | null>(null);
  if (rows.length === 0) return null;
  const peak = Math.max(1, ...rows.map((v) => v.v_best_m3 ?? 0));
  const max = peak * 1.25;
  const W = 720;
  const H = 120;
  const gap = 4;
  const bw = (W - gap * (rows.length - 1)) / rows.length;
  const y = (v: number) => H - (Math.min(max, Math.max(0, v)) / max) * H;
  const shown = hover ?? null;
  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H + 18}`}
        width="100%"
        style={{ display: "block", maxHeight: 170 }}
        role="img"
        aria-label="Volume per sector"
        onMouseLeave={() => setHover(null)}
      >
        {[0.5, 1].map((f) => (
          <line key={f} x1={0} x2={W} y1={H - f * H} y2={H - f * H} stroke={palette.grid} strokeWidth={1} />
        ))}
        {rows.map((v, i) => {
          const x = i * (bw + gap);
          const best = v.v_best_m3 ?? 0;
          const top = y(best);
          const unseen = v.unobserved_frac ?? 0;
          return (
            <g key={v.scope_id} onMouseEnter={() => setHover(v)} style={{ cursor: "default" }}>
              <rect x={x} y={0} width={bw} height={H} fill="transparent" />
              <rect
                x={x}
                y={top}
                width={bw}
                height={Math.max(0, H - top)}
                rx={3}
                fill={palette.brand[6]}
                fillOpacity={best > 0 ? 0.35 + 0.6 * (1 - Math.min(1, unseen)) : 0}
              />
              {best >= 0.02 * peak && v.v_max_m3 != null && (
                <line
                  x1={x + bw / 2}
                  x2={x + bw / 2}
                  y1={y(v.v_max_m3)}
                  y2={y(Math.max(0, v.v_min_m3 ?? best))}
                  stroke={palette.text}
                  strokeOpacity={0.45}
                  strokeWidth={1.5}
                />
              )}
              <text x={x + bw / 2} y={H + 13} fontSize={11} textAnchor="middle" fill={palette.muted}>
                {v.scope_id}
              </text>
            </g>
          );
        })}
      </svg>
      <Group justify="space-between" mt={6} wrap="nowrap">
        <Text size="xs" c="dimmed">
          {shown
            ? `Sector ${shown.scope_id}: ${fmt0(shown.v_best_m3)} m³ (${fmtRange(shown.v_min_m3, shown.v_max_m3)}) · ${fmt0(shown.tonnes_t)} t · ${fmtPct(shown.unobserved_frac)} unseen`
            : "Hover a bar for its sector's volume, range and tonnes."}
        </Text>
        <Text size="xs" c="dimmed">
          peak {fmt0(peak)} m³
        </Text>
      </Group>
    </div>
  );
}
